/**
 * Hook for managing Event Survey Response mutations (create/update)
 *
 * This hook handles all API calls for creating and updating event survey responses.
 * It follows the pattern established in SurveyCreation containers.
 *
 * @see packages/main/app/web/containers/SurveyCreation/EventRegistrationSurvey/hooks/useEventRegistrationSurveyMutations.ts
 */

import * as Api from '@ViewModels';
import { toast } from 'sonner';
import {
	useClientCreateEventRegistrationResponseMutation,
	useClientUpdateEventRegistrationResponseMutation,
} from '../../../../../../../../queries';
import type { IEventSurveyResponse } from '../../../../../../../models/Api';

interface UseEventSurveyMutationsProps {
	formValues: {
		isAttending: boolean | null;
	};
	onResponseSubmitted?: (response: IEventSurveyResponse) => void;
	setIsSubmitting: (loading: boolean) => void;
}

export const useEventSurveyMutations = ({
	formValues,
	onResponseSubmitted,
	setIsSubmitting,
}: UseEventSurveyMutationsProps) => {
	const handleMutationSuccess = ({ successMessage, data }: { successMessage: string; data: IEventSurveyResponse }) => {
		toast.success(successMessage);
		if (onResponseSubmitted) {
			onResponseSubmitted(data);
		}
		// Don't open checkout or clear loading here - let useEffect handle timing after state updates
		// Modal opening moved to useEffect to ensure state sync completes first
	};

	const createResponseMutation = useClientCreateEventRegistrationResponseMutation({
		onError: (e: Api.IOperationResultNoValue) => {
			toast.error(e?.systemMessage);
			setIsSubmitting(false);
		},
		onSuccess: (data: IEventSurveyResponse) => {
			// Preserve the isAttending value from form
			data.isAttending = formValues.isAttending;
			handleMutationSuccess({
				successMessage: 'RSVP successfully submitted',
				data,
			});
		},
	});

	const updateResponseMutation = useClientUpdateEventRegistrationResponseMutation({
		onError: (e: Api.IOperationResultNoValue) => {
			toast.error(e?.systemMessage);
			setIsSubmitting(false);
		},
		onSuccess: (data: IEventSurveyResponse) => {
			handleMutationSuccess({
				successMessage: 'RSVP successfully updated',
				data,
			});
		},
	});

	return {
		createResponseMutation,
		updateResponseMutation,
	};
};
